import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { FiSend, FiKey } from 'react-icons/fi';
import { BsShieldLock } from 'react-icons/bs';
import { END_POINT, getJwtToken } from '../../Redux/AdminReducer/action';
import { Button, Input, Modal } from '../../Components/UI';

const authHeaders = () => ({ headers: { Authorization: getJwtToken() } });

const getUsers = async (query) => {
  try {
    const result = await axios.get(`${END_POINT}/configAccess/users/getOrgUsers/?${query}`, authHeaders());
    return Array.isArray(result?.data) ? result.data : [];
  } catch (error) {
    toast.error(error?.response?.data || 'Failed to fetch users');
    return [];
  }
};

const ResetPasswordPopup = ({ isOpen, onClose }) => {
  const loggedInUser = useSelector((state) => state?.AuthReducer?.userLogindata?.data);
  const isMainDoctor = loggedInUser?.role === 'MainDoctor';

  const [orgUsers, setOrgUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isSendingLink, setIsSendingLink] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  const selectedUser = orgUsers.find((u) => u.user_id === selectedUserId);

  const resetFields = useCallback(() => {
    setNewPassword('');
    setConfirmPassword('');
    setFormError({});
  }, []);

  // A MainDoctor can act on anyone in the clinic; everyone else only on themselves.
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    setIsLoadingUsers(true);

    getUsers(isMainDoctor ? 'uid=all' : `uid=${loggedInUser?._id}`).then((users) => {
      if (cancelled) return;
      setOrgUsers(users);
      setSelectedUserId(
        users.find((u) => u.user_id === loggedInUser?._id)?.user_id || users?.[0]?.user_id || ''
      );
      setIsLoadingUsers(false);
    });

    return () => {
      cancelled = true;
    };
  }, [isOpen, isMainDoctor, loggedInUser?._id]);

  const handleClose = () => {
    resetFields();
    onClose();
  };

  const validate = () => {
    const errors = {};
    if (!selectedUser?.user_id) errors.user = 'Please select a user';
    if (!newPassword) {
      errors.newPassword = 'Enter a new password';
    } else if (newPassword.trim().length < 6) {
      errors.newPassword = 'Password must be at least 6 characters';
    }
    if (newPassword !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    setFormError(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSetPassword = async () => {
    if (!validate()) return;

    setIsSaving(true);
    try {
      await axios.post(
        `${END_POINT}/resetPassword/${selectedUser.user_id}/${selectedUser.role}`,
        { newPassword: newPassword.trim() },
        authHeaders()
      );
      toast.success('Password changed successfully');
      resetFields();
      onClose();
    } catch (error) {
      toast.error(error?.response?.data || 'Something went wrong');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendResetLink = async () => {
    if (!selectedUser?.user_id) {
      setFormError({ user: 'Please select a user' });
      return;
    }

    setIsSendingLink(true);
    try {
      const res = await axios.post(
        `${END_POINT}/resetPassword/forgotPassword/${selectedUser.user_id}/${selectedUser.role}`,
        { origin: window.location.origin },
        authHeaders()
      );
      toast.success(res?.data?.msg || 'Reset link sent');
      resetFields();
      onClose();
    } catch (error) {
      toast.error(error?.response?.data || 'Could not send the reset link');
    } finally {
      setIsSendingLink(false);
    }
  };

  const isBusy = isSaving || isSendingLink;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Reset Password"
      subtitle={
        isMainDoctor
          ? 'Set a new password for any clinic user, or email them a reset link'
          : 'Choose a new password for your account'
      }
      maxWidth="max-w-md"
      footer={
        <>
          <Button variant="outline" size="sm" onClick={handleClose} disabled={isBusy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSetPassword}
            isLoading={isSaving}
            disabled={isSendingLink}
            icon={<FiKey className="w-4 h-4" />}
          >
            Update Password
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {isMainDoctor && (
          <div className="w-full">
            <label
              htmlFor="reset-user"
              className="block mb-1.5 text-xs font-semibold text-slate-700 tracking-wide"
            >
              User<span className="text-rose-500 ml-1">*</span>
            </label>
            <select
              id="reset-user"
              value={selectedUserId}
              onChange={(e) => {
                setSelectedUserId(e.target.value);
                setFormError((prev) => ({ ...prev, user: null }));
              }}
              disabled={isLoadingUsers || isBusy}
              className={`w-full rounded-xl border bg-white px-3.5 py-2.5 text-sm text-slate-800 shadow-sm outline-none transition-all duration-150 disabled:bg-slate-100 disabled:text-slate-500 ${
                formError.user
                  ? 'border-rose-400 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20'
                  : 'border-slate-200 hover:border-slate-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20'
              }`}
            >
              {isLoadingUsers && <option value="">Loading users…</option>}
              {!isLoadingUsers && orgUsers.length === 0 && (
                <option value="">No users found</option>
              )}
              {!isLoadingUsers &&
                orgUsers.map((user) => (
                  <option key={user.user_id} value={user.user_id}>
                    {user.username} — {user.role}
                    {user.user_id === loggedInUser?._id ? ' (you)' : ''}
                  </option>
                ))}
            </select>
            {formError.user && (
              <p className="mt-1.5 text-xs text-rose-500 font-medium">{formError.user}</p>
            )}
          </div>
        )}

        {!isMainDoctor && (
          <div className="flex items-center gap-3 rounded-xl bg-slate-50 border border-slate-200 px-3.5 py-2.5">
            <BsShieldLock className="w-4 h-4 text-sky-600 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-800 truncate">
                {selectedUser?.username || loggedInUser?.name || 'Your account'}
              </p>
              <p className="text-xs text-slate-500 truncate">
                {selectedUser?.email || loggedInUser?.email || ''}
              </p>
            </div>
          </div>
        )}

        <Input
          label="New Password"
          name="newPassword"
          type="password"
          placeholder="At least 6 characters"
          value={newPassword}
          onChange={(e) => {
            setNewPassword(e.target.value);
            setFormError((prev) => ({ ...prev, newPassword: null }));
          }}
          error={formError.newPassword}
          disabled={isBusy}
          required
        />

        <Input
          label="Confirm Password"
          name="confirmPassword"
          type="password"
          placeholder="Re-enter the new password"
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            setFormError((prev) => ({ ...prev, confirmPassword: null }));
          }}
          error={formError.confirmPassword}
          disabled={isBusy}
          required
        />

        <div className="pt-1 border-t border-slate-100">
          <p className="text-xs text-slate-500 mt-3 mb-2">
            Prefer not to type a password? Email a reset link instead — it expires in 30 minutes.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSendResetLink}
            isLoading={isSendingLink}
            disabled={isSaving || !selectedUser?.user_id}
            icon={<FiSend className="w-4 h-4" />}
          >
            {isMainDoctor && selectedUser?.user_id !== loggedInUser?._id
              ? 'Send reset link to this user'
              : 'Email me a reset link'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ResetPasswordPopup;
